// absolute imports
import { Avatar, ButtonBase, MenuItem, MenuList } from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

// icon imports
import {
  ExitToApp as ExitToAppIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';

// relative imports
import { useAccount } from '../../providers/AccountProvider';
import { useLedgibleAuth } from '../../providers/LedgibleAuthProvider';
import ExtendedPopper from '../custom-components/ExtendedPopper';
import { getComponentTestKey } from '../utilities/testUtilities';

const useStyles = makeStyles()((theme) => ({
  avatar: {
    width: 35,
    height: 35,
  },
  menuList: {
    padding: theme.spacing(0),
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: theme.typography.pxToRem(14),
    '&:hover': {
      color: '#000',
      textDecoration: 'none',
    },
    minWidth: 120,
  },
  linkItem: {
    '&:hover': {
      color: 'inherit',
    },
  },
  menuIcon: {
    marginRight: theme.spacing(1),
  },
}));

const ProfileNavbox = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { classes, cx } = useStyles();
  const { user, logout } = useLedgibleAuth();
  const accountData = useAccount();
  const { showApplicationTypeSelection } = accountData || {};
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Show "Switch Application Type" option if user has multiple application types
  const canSwitchAccountType =
    (user?.availableAccountApplicationTypes?.length || 0) > 1;

  const handleToggleMenu = (event) => {
    setOpen((prevOpen) => !prevOpen);
    anchorRef.current = event.target;
  };

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      event &&
      anchorRef.current.contains(event.target)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleUserSettingsClick = () => {
    if (pathname !== '/user-settings') {
      navigate({
        to: '/user-settings',
        state: {
          redirect: pathname,
          redirectLabel: 'Back',
        },
      });
    }
    handleClose();
  };

  const handleSwitchAccountType = () => {
    showApplicationTypeSelection();
    handleClose();
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  return (
    <ExtendedPopper
      content={
        <MenuList disableListWrap className={classes.menuList}>
          <MenuItem
            onClick={handleUserSettingsClick}
            className={cx(classes.menuItem, classes.linkItem)}
            divider
          >
            <PersonIcon className={classes.menuIcon} fontSize="small" />
            User Settings
          </MenuItem>
          {canSwitchAccountType && (
            <MenuItem
              onClick={handleSwitchAccountType}
              className={cx(classes.menuItem, classes.linkItem)}
              divider
              data-cy={getComponentTestKey({
                componentName: 'ProfileNavbox',
                elementType: 'button',
                elementName: 'switchApplicationType',
              })}
            >
              <SwapHorizIcon className={classes.menuIcon} fontSize="small" />
              Switch Application Type
            </MenuItem>
          )}
          <MenuItem
            onClick={handleLogout}
            className={classes.menuItem}
            data-cy={getComponentTestKey({
              componentName: 'ProfileNavbox',
              elementType: 'button',
              elementName: 'logout',
            })}
          >
            <ExitToAppIcon className={classes.menuIcon} />
            Logout
          </MenuItem>
        </MenuList>
      }
      arrow
      open={open}
      placement="bottom-start"
      clickAwayHandler={handleClose}
      padContent={false}
    >
      <ButtonBase
        disableRipple
        ref={anchorRef}
        aria-controls="menu-list-grow"
        aria-haspopup="true"
        onClick={handleToggleMenu}
        id="ProfileNavBoxButton"
        data-cy="profile-menu-button"
        title={`${user.profile.username || 'profile'} user menu`}
      >
        <Avatar
          alt="Profile Picture"
          src={user.profile.picture}
          className={cx(classes.avatar, classes.bigAvatar)}
          data-cy="profile-menu-avatar"
        />
      </ButtonBase>
    </ExtendedPopper>
  );
};

export default ProfileNavbox;
